import { parseAbi } from 'viem'
import { MulticallRetryFunction } from '../types'

/** Minimal ERC-20 surface for reading an underlying's own decimals. */
const ERC20_DECIMALS_ABI = parseAbi([
  'function decimals() view returns (uint8)',
])

/**
 * Read each underlying token's ERC-20 `decimals()`, keyed by lowercased
 * address. Used by the on-chain vault fetchers to format `totalAssets`
 * (denominated in *underlying* units) with the asset's own decimals rather
 * than the vault's share `decimals()` — which, for ERC-4626 vaults with a
 * decimals offset (e.g. MetaMorpho's 18-decimal shares over a 6-decimal
 * asset), would blow the formatted value up/down by 10^offset.
 *
 * `allowFailure` + range-checked so a flaky read on one token can't poison the
 * batch; callers fall back to the share decimals when an entry is missing.
 */
export async function fetchAssetDecimals(
  chainId: string,
  underlyings: string[],
  multicallRetry: MulticallRetryFunction,
): Promise<Map<string, number>> {
  const map = new Map<string, number>()
  if (underlyings.length === 0) return map
  const raw = await multicallRetry({
    chain: chainId,
    calls: underlyings.map((address) => ({
      address,
      name: 'decimals',
      params: [],
    })),
    abi: underlyings.map(() => ERC20_DECIMALS_ABI) as any,
    allowFailure: true,
  })
  underlyings.forEach((address, i) => {
    const d = Number((raw as unknown[])[i])
    if (Number.isFinite(d) && d > 0 && d <= 36) map.set(address, d)
  })
  return map
}
